import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save, User, Mail, Lock, Briefcase, IdCard, Eye, EyeOff, Building2, Shield } from 'lucide-react';
import { USER_ROLES } from '../../utils/constants';
import api from '../../utils/api';
import { useFormValidation } from '../../hooks/useFormValidation';
import { VALIDATION_SCHEMAS } from '../../utils/validators';
import { getAvatarUrl } from '../../utils/externalUrls';


const UserForm = ({ user = null, onSave, onClose }) => {
  const initialData = {
    name: user?.name || '',
    lastName: user?.lastName || '',
    idCard: user?.idCard || '',
    username: user?.username || '',
    email: user?.email || '',
    role: user?.role || 'asesor',
    password: '',
    confirmPassword: '',
    active: user?.active !== undefined ? user.active : true,
    avatar: user?.avatar || getAvatarUrl('default'),
    officeId: user?.officeId || '',
    isSuperAdmin: user?.isSuperAdmin || false
  };

  const {
    formData,
    setFormData,
    errors,
    updateField,
    validateForm,
    validatePasswordMatch,
    clearErrors,
    getSanitizedData,
    hasFieldError,
    getFieldError
  } = useFormValidation(initialData, VALIDATION_SCHEMAS.USER);

  const [showPassword, setShowPassword] = useState(false);
  const [offices, setOffices] = useState([]);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    // Fetch offices
    const fetchOffices = async () => {
      try {
        const response = await api.get('/offices');
        setOffices(response.data);
        logger.debug('Offices loaded:', response.data);
      } catch (error) {
        logger.error('Error fetching offices:', error.response?.data?.message || error.message);
      }
    };
    fetchOffices();

    if (user) {
      setFormData({
        name: user.name,
        lastName: user.lastName || '',
        idCard: user.idCard || '',
        username: user.username || '',
        email: user.email,
        role: user.role,
        password: '',
        active: user.active,
        avatar: user.avatar,
        officeId: user.officeId || '',
        isSuperAdmin: user.isSuperAdmin || false
      });
    } else {
      setFormData(prev => ({
        ...prev,
        avatar: getAvatarUrl(Date.now().toString())
      }));
    }
  }, [user]);

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "La contraseña debe tener al menos 8 caracteres.";
    }
    if (!/[A-Z]/.test(password)) {
      return "La contraseña debe contener al menos una letra mayúscula.";
    }
    if (!/[0-9]/.test(password)) {
      return "La contraseña debe contener al menos un número.";
    }
    return ""; // No error
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!user || formData.password) { // Validate password only if new user or password field is not empty for existing user
      const error = validatePassword(formData.password);
      if (error) {
        setPasswordError(error);
        return;
      }
    }
    setPasswordError(''); // Clear any previous errors

    const userData = {
      ...formData,
      ...(user ? { id: user.id } : {}),
    };

    // No enviar username al crear nuevo usuario (se auto-genera en backend)
    if (!user) {
      delete userData.username;
    }

    onSave(userData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    // Usar updateField del hook para mantener consistencia
    updateField(name, fieldValue);

    if (name === 'password') {
      setPasswordError(validatePassword(value));
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b border-gray-200 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <motion.button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-6 h-6" />
          </motion.button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre
            </label>
            <div className="relative">
              <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nombre del usuario"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellido
            </label>
            <div className="relative">
              <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Apellido del usuario"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cédula
            </label>
            <div className="relative">
              <IdCard className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="idCard"
                value={formData.idCard}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Número de cédula"
              />
            </div>
          </div>

          {/* Mostrar username solo en modo edición */}
          {user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Código de Asesor
              </label>
              <div className="relative">
                <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  placeholder="Auto-generado"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Este código se genera automáticamente y no se puede modificar</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Correo Electrónico
            </label>
            <div className="relative">
              <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="correo@ejemplo.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rol
            </label>
            <div className="relative">
              <Briefcase className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.entries(USER_ROLES).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Mostrar campo Super Admin solo si el rol es administrador */}
          {formData.role === 'administrador' && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="isSuperAdmin"
                name="isSuperAdmin"
                checked={formData.isSuperAdmin}
                onChange={handleChange}
                className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="isSuperAdmin" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Jefe / Super Administrador (acceso completo)
              </label>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Oficina Asignada
            </label>
            <div className="relative">
              <Building2 className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <select
                name="officeId"
                value={formData.officeId}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Sin oficina asignada</option>
                {offices.map(office => (
                  <option key={office.id} value={office.id}>{office.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!user} 
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={user ? "Dejar en blanco para no cambiar" : "Contraseña"}
              />
              <motion.button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </motion.button>
            </div>
            {passwordError && (
              <p className="text-red-500 text-sm mt-1">{passwordError}</p>
            )}
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="active"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="active" className="text-sm font-medium text-gray-700">
              Usuario Activo
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
            <motion.button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancelar
            </motion.button>
            <motion.button
              type="submit"
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Save className="w-5 h-5" />
              {user ? 'Actualizar' : 'Crear'} Usuario
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default UserForm;